import React, { useState } from 'react';
import {
    Button,
    Divider,
    FormControlLabel,
    Menu,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import CaretDown from '@mds/icons/CaretDown';
import SlidersHorizontal from '@mds/icons/SlidersHorizontal';
import { SortType, TypeFilter } from '../../types';
import { useFilter } from '../../FilterContext';

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="caption"
        sx={{
            color: 'text.secondary',
            display: 'block',
            px: 2,
            pt: 1,
            pb: 0.5,
        }}
    >
        {children}
    </Typography>
);

const radioLabel = (label: string) => (
    <Typography variant="body2">{label}</Typography>
);

const FilterAndSortMenu: React.FC = () => {
    const {
        sortType,
        setSortType,
        typeFilter,
        setTypeFilter,
        resetSortTypeToDefault,
    } = useFilter();
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(
        null
    );
    const isOpen = Boolean(anchorElement);

    const openMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElement(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchorElement(null);
    };

    return (
        <>
            <Button
                variant="outlined"
                color="secondary"
                size="small"
                startIcon={<SlidersHorizontal fontSize="small" />}
                endIcon={<CaretDown fontSize="small" />}
                onClick={openMenu}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                Sort & View
            </Button>
            <Menu
                open={isOpen}
                anchorEl={anchorElement}
                onClose={closeMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 240,
                            mt: 0.5,
                            pb: 2,
                        },
                    },
                }}
            >
                <SectionLabel>Sort By</SectionLabel>
                <RadioGroup
                    value={sortType}
                    onChange={(event) =>
                        setSortType(event.target.value as SortType)
                    }
                >
                    <Stack sx={{ px: 2 }}>
                        <FormControlLabel
                            value={SortType.None}
                            control={<Radio size="small" />}
                            label={radioLabel('None')}
                        />
                        <FormControlLabel
                            value={SortType.AZ}
                            control={<Radio size="small" />}
                            label={radioLabel('A-Z')}
                        />
                        <FormControlLabel
                            value={SortType.ZA}
                            control={<Radio size="small" />}
                            label={radioLabel('Z-A')}
                        />
                    </Stack>
                </RadioGroup>
                <Divider sx={{ mx: 2, my: 1 }} />
                <SectionLabel>View</SectionLabel>
                <RadioGroup
                    value={typeFilter}
                    onChange={(event) =>
                        setTypeFilter(event.target.value as TypeFilter)
                    }
                >
                    <Stack sx={{ px: 2 }}>
                        <FormControlLabel
                            value={TypeFilter.All}
                            control={<Radio size="small" />}
                            label={radioLabel('All')}
                        />
                        <FormControlLabel
                            value={TypeFilter.Premium}
                            control={<Radio size="small" />}
                            label={radioLabel('Premium')}
                        />
                        <FormControlLabel
                            value={TypeFilter.Free}
                            control={<Radio size="small" />}
                            label={radioLabel('Free')}
                        />
                    </Stack>
                </RadioGroup>
                <Divider sx={{ mx: 2, my: 1 }} />
                <Stack sx={{ px: 2 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        onClick={resetSortTypeToDefault}
                    >
                        Reset to default
                    </Button>
                </Stack>
            </Menu>
        </>
    );
};

export default FilterAndSortMenu;
